// v49.3 / v50.185 — Minimal toast system.
// Replaces the v48.x lcToast function. Provider sits at app root;
// useToast() returns a `toast({ kind, msg, subtitle })` function.
// Auto-dismiss after 4s; click to dismiss immediately.
//
// v50.185 — Monzo-style money treatment. Pass `money: { pence, direction }`
// to surface the £ amount as the prominent line (think Monzo push: a big
// + £42.50 with the context underneath). Direction "in" reads green
// with a + sign; "out" reads red with a - sign. Plain toasts still work
// exactly as before.

import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

type ToastKind = "success" | "warn" | "info";

interface MoneyDetail {
  pence:     number;
  /* in = money received (partner viewpoint on a paid invoice);
     out = money paid (coord viewpoint). */
  direction: "in" | "out";
}

interface Toast {
  id:        number;
  kind:      ToastKind;
  msg:       string;
  subtitle?: string;
  /* v50.16 — optional click action. When set, tapping the toast fires
     onClick AND dismisses; otherwise tap-dismisses as before. */
  onClick?:  () => void;
  /* v50.185 — Monzo money treatment. When set, the toast renders the
     amount as the prominent line with a green/red direction prefix.
     msg + subtitle stay below as the context. */
  money?:    MoneyDetail;
}

interface ToastContextValue {
  toast: (t: Omit<Toast, "id">) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);

  const toast = useCallback((t: Omit<Toast, "id">) => {
    const id = Date.now() + Math.random();
    setItems(prev => [...prev, { ...t, id }]);
    setTimeout(() => { setItems(prev => prev.filter(x => x.id !== id)); }, 4000);
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {createPortal(
        /* v50.180 — WCAG 2.2 AA: announce toasts to assistive tech.
           role="region" + aria-label names the area; aria-live="polite"
           queues each new toast for SR readout without interrupting
           current speech. aria-atomic={false} means each toast is
           announced individually rather than the whole list re-read on
           every change. */
        <div
          role="region"
          aria-label="Notifications"
          aria-live="polite"
          aria-atomic="false"
          className="fixed bottom-5 right-5 z-[2000] flex flex-col gap-2 max-w-sm pointer-events-none"
        >
          {items.map(it => <ToastCard key={it.id} item={it} onDismiss={() => setItems(prev => prev.filter(x => x.id !== it.id))} />)}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): (t: Omit<Toast, "id">) => void {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    /* Best-effort fallback when used outside the provider — log to
       console so the caller doesn't crash. */
    return (t) => console.log("[toast]", t.kind, t.msg, t.subtitle || "");
  }
  return ctx.toast;
}

function ToastCard({ item, onDismiss }: { item: Toast; onDismiss: () => void }) {
  const tone = item.kind === "success"
    ? { bg: "bg-lc-green-bg",  fg: "text-lc-green-fg",  border: "border-green-200", dot: "bg-lc-green" }
    : item.kind === "warn"
    ? { bg: "bg-lc-amber-bg",  fg: "text-lc-amber-fg",  border: "border-amber-200", dot: "bg-lc-amber" }
    : { bg: "bg-lc-blue-light",fg: "text-lc-blue-dark", border: "border-lc-blue-border", dot: "bg-lc-blue" };
  /* v50.16 — when onClick is set, tap fires the action then dismisses.
     The card label shifts from a plain rect to one that visually invites
     clicking via a small "→" tail. */
  const handle = () => {
    try { item.onClick?.(); } catch { /* ignore */ }
    onDismiss();
  };
  /* v50.185 — money treatment renders an amount headline using the
     in/out direction colour. Pounds shown with pence; reads like a
     Monzo notification. Plain toasts skip this block entirely. */
  const money = item.money;
  const moneyClasses = money?.direction === "in"
    ? { fg: "text-lc-green-fg", sign: "+" }
    : { fg: "text-lc-red-fg",   sign: "−" };
  return (
    <button
      type="button"
      onClick={handle}
      className={`pointer-events-auto text-left px-4 py-3 rounded-lc-md shadow-lg border ${tone.bg} ${tone.border} ${tone.fg} flex items-start gap-2.5 cursor-pointer animate-[fadeIn_.15s_ease-out] hover:brightness-105 min-w-[260px]`}
    >
      <div className={`w-2 h-2 rounded-full ${tone.dot} mt-1.5 shrink-0`} />
      <div className="flex-1 min-w-0">
        {money && (
          <div
            className={`text-xl font-black tracking-tight tabular-nums leading-tight ${moneyClasses.fg}`}
            aria-label={`${money.direction === "in" ? "Received" : "Paid"} ${(money.pence / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} pounds`}
          >
            <span aria-hidden="true">{moneyClasses.sign} </span>
            <span aria-hidden="true">£{(money.pence / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        )}
        <div className={`text-sm font-extrabold leading-tight ${money ? "mt-1" : ""}`}>{item.msg}</div>
        {item.subtitle && <div className="text-xs mt-0.5 leading-snug opacity-80">{item.subtitle}</div>}
      </div>
      {item.onClick && <span className="text-sm font-extrabold opacity-70 mt-0.5">→</span>}
    </button>
  );
}
